import cx from "classnames";
import { FC, MouseEventHandler, ReactNode, SVGProps } from "react";

import { useHaptics } from "@/modules/haptics/useHaptics/useHaptics";

import { Icon } from "../icon/Icon";
import "./Button.css";
import "./ButtonShape.css";

type ButtonSize = "xsmall" | "small" | "medium" | "large" | "xlarge";
type ButtonStyle =
  | "primary"
  | "white"
  | "outline"
  | "secondary"
  | "secondarySolid"
  | "danger"
  | "dangerSolid"
  | "adonisColor"
  | "border";
type ButtonIconSize = "x3small" | "x2small" | "xsmall" | "small" | "medium" | "large" | "xlarge";

interface ButtonProps {
  "children": ReactNode;
  "disabled"?: boolean;
  "fullWidth"?: boolean;
  "onClick"?: MouseEventHandler<HTMLButtonElement>;
  "size"?: ButtonSize;
  "startIconSVG"?: FC<SVGProps<SVGSVGElement>>;
  "endIconSVG"?: FC<SVGProps<SVGSVGElement>>;
  "style"?: ButtonStyle;
  "submit"?: boolean;
  "data-testid"?: string;
  "iconSize"?: ButtonIconSize;
  "hapticType"?: "auto" | "primary" | "secondary" | "none";
  "hapticsDisabled"?: boolean;
}

export const Button = ({
  children,
  disabled,
  fullWidth,
  onClick,
  size = "medium",
  startIconSVG,
  endIconSVG,
  style = "primary",
  submit = false,
  "data-testid": dataTestId,
  iconSize = "medium",
  hapticType = "auto",
  hapticsDisabled = false,
}: ButtonProps) => {
  const styles = {
    primary:
      "animated-gradient-button text-white shadow-md enabled:active:shadow-lg enabled:hover:shadow-lg",
    white:
      "bg-white text-brand-color enabled:active:bg-white/90 enabled:hover:bg-gradient-to-t enabled:hover:from-black/5 enabled:hover:to-black/5",
    outline:
      "border border-white text-white enabled:active:bg-white/5 enabled:hover:bg-black/5 shadow text-shadow",
    secondary: "border border-brand-color text-brand-color enabled:hover:bg-black/2",
    secondarySolid:
      "border border-brand-color bg-brand-color text-white enabled:hover:bg-brand-color/90 text-chip uppercase",
    border:
      "border border-[#E7E8E8] enabled:active:bg-brand-color/30 enabled:hover:bg-brand-color/30",
    danger: "border border-red text-red enabled:hover:bg-black/2",
    dangerSolid: "border border-red bg-red text-white enabled:hover:bg-red/90",
    adonisColor: "bg-[#BDA280] text-white enabled:hover:bg-[#b29671]",
  };

  const sizes = {
    xsmall: "min-h-8 !px-3.5 text-bold2",
    small: "min-h-10",
    medium: "min-h-11",
    large: "min-h-[50px]",
    xlarge: "min-h-14",
  };

  const { triggerHaptic } = useHaptics();
  const resolveHaptic = (): "primary" | "secondary" | "error" | "none" => {
    if (hapticType !== "auto") {
      return hapticType;
    }
    if (style === "primary") {
      return "primary";
    }
    if (style === "danger" || style === "dangerSolid") {
      return "error";
    }
    if (style === "secondary" || style === "secondarySolid" || style === "outline") {
      return "secondary";
    }
    return "none";
  };
  const handleClick: MouseEventHandler<HTMLButtonElement> = async (e) => {
    if (!disabled) {
      // Fire-and-forget: don’t block click on haptics
      void triggerHaptic(resolveHaptic(), { disabled: hapticsDisabled }).catch(() => undefined);
    }
    onClick?.(e);
  };
  return (
    <button
      type={submit ? "submit" : "button"}
      className={cx(
        "flex",
        "items-center",
        "justify-center",
        "button-continuous",
        startIconSVG ? "pl-4 pr-6" : "px-6",
        endIconSVG ? "pl-6 pr-4" : "px-6",
        "font-sans",
        "enabled:active:opacity-50",
        "disabled:opacity-20",
        // `fullWidth` fills the parent, on every viewport. Columns that would
        // otherwise stretch a CTA across the page cap it themselves (see
        // `ReferAFriend`, `MembershipsPromo`); a control capping itself leaves
        // it floating out of line with the rest of its column.
        fullWidth ? "w-full" : "w-fit",
        styles[style],
        sizes[size],
        style === "border" ? "text-body2" : "text-body1",
      )}
      disabled={disabled}
      onClick={handleClick}
      data-testid={dataTestId}
    >
      {startIconSVG ? (
        <div className="flex items-center justify-center">
          <div className="pr-2">
            <Icon svg={startIconSVG} size={iconSize} color="currentColor" />
          </div>
          {children}
        </div>
      ) : (
        children
      )}
      {endIconSVG ? (
        <div className="flex items-center justify-between">
          <div className="pr-2">
            <Icon svg={endIconSVG} size={iconSize} color="currentColor" />
          </div>
        </div>
      ) : null}
    </button>
  );
};
